import React, { useEffect, useMemo, useState } from 'react';
import Table from 'apollo-react/components/Table';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Typography from '../StyledTypography';
import CommonModal from '../CommonModal/CommonModal';
import { useNavigate } from 'react-router-dom';
import usePermissions from '../../../hooks/features/permissions/usePermissions';
import axiosInstance from '../../../axiosInstance';
import { useActiveTab } from '../../../contexts/ActiveTabContext';
import { TAB_STATE_KEYS } from '../../../constants/request.constant';
import { buildDashboardDateQueryParams } from '../../../utils/dashboardDateRangeUtils';

export const TENDER_LIST_STATUS = {
  SUBMITTED: 'submitted',
  PENDING: 'pending',
  AWARDED: 'awarded',
  LOST: 'lost',
  WIN_RATE: 'win-rate',
};

/** Pipeline bar label -> list status. */
export const TENDER_STATUS_BY_LABEL = {
  Submitted: TENDER_LIST_STATUS.SUBMITTED,
  Pending: TENDER_LIST_STATUS.PENDING,
  Awarded: TENDER_LIST_STATUS.AWARDED,
  Lost: TENDER_LIST_STATUS.LOST,
};

const MODAL_TITLES = {
  [TENDER_LIST_STATUS.SUBMITTED]: 'Tender pipeline — Submitted requests',
  [TENDER_LIST_STATUS.PENDING]: 'Tender pipeline — Pending requests',
  [TENDER_LIST_STATUS.AWARDED]: 'Tender pipeline — Awarded requests',
  [TENDER_LIST_STATUS.LOST]: 'Tender pipeline — Lost and cancelled requests',
  [TENDER_LIST_STATUS.WIN_RATE]: 'Win rate — Tenders won and lost',
};

const OUTCOME_LABELS = {
  awarded: 'Awarded',
  cancelled: 'Cancelled',
  lost: 'Lost',
  pending: 'Pending',
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : '-';

/** Awarded requests keep the exact field value (e.g. "Partially Awarded"). */
const outcomeLabel = (row) =>
  row.outcome === 'awarded' && row.outcome_value
    ? row.outcome_value
    : OUTCOME_LABELS[row.outcome] || '-';

/**
 * Lists the requests behind a Tender pipeline bar, or the won/lost tenders
 * the Win rate percentage is calculated from.
 */
const TenderRequestsModal = ({
  status,
  winRate,
  dateFilter,
  persona,
  onClose,
}) => {
  const navigate = useNavigate();
  const { setActiveTab: setApprovalsTab } = useActiveTab(
    TAB_STATE_KEYS.ADMIN_APPROVALS
  );
  const {
    userContext: { isSuper },
  } = usePermissions();
  const isEndUser = persona === 'enduser';
  const requestPathPrefix = isEndUser
    ? '/enduser/progress/requests'
    : '/admin/review';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!status) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    axiosInstance
      .get(
        isEndUser
          ? '/api/dashboard/tender-requests/enduser'
          : '/api/dashboard/tender-requests',
        {
          params: { status, ...buildDashboardDateQueryParams(dateFilter) },
          withCredentials: true,
        }
      )
      .then((res) => {
        if (!cancelled) setRows(res.data?.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load requests');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, dateFilter, isEndUser]);

  const columns = useMemo(
    () => [
      {
        header: 'Request Name',
        accessor: 'study_name',
        customCell: ({ row }) => row.study_name || row.generated_id || '-',
      },
      { header: 'ID', accessor: 'generated_id' },
      {
        header: 'Workflow',
        accessor: 'workflow_name',
        customCell: ({ row }) => row.workflow_name || '-',
      },
      {
        header: 'Submission Date',
        accessor: 'date_started',
        customCell: ({ row }) => formatDate(row.date_started),
      },
      {
        header: 'Award Outcome',
        accessor: 'outcome',
        customCell: ({ row }) => outcomeLabel(row),
      },
      {
        header: 'Action',
        accessor: 'id',
        customCell: ({ row }) =>
          // Super admins can't open request details: send them to the
          // Approvals list on the tab that holds this request.
          isSuper && row.approvals_tab != null ? (
            <a
              href={`/admin/approvals/requests?highlight=${row.id}`}
              onClick={(event) => {
                event.preventDefault();
                setApprovalsTab(row.approvals_tab);
                navigate(`/admin/approvals/requests?highlight=${row.id}`);
              }}
            >
              View in approvals
            </a>
          ) : (
            <a
              href={`${requestPathPrefix}/${row.id}`}
              target="_blank"
              rel="noreferrer"
            >
              Review
            </a>
          ),
      },
    ],
    [requestPathPrefix, navigate, setApprovalsTab, isSuper]
  );

  const isWinRate = status === TENDER_LIST_STATUS.WIN_RATE;

  return (
    <CommonModal
      open={Boolean(status)}
      onClose={onClose}
      title={MODAL_TITLES[status] || 'Tender requests'}
      size="large"
      buttonProps={[{ label: 'Close', onClick: onClose }]}
    >
      {isWinRate && winRate ? (
        <Typography variant="bodyDefault" style={{ marginBottom: 12 }}>
          {winRate.percent != null
            ? `Win rate ${winRate.percent}% — ${winRate.won} won of ${winRate.decided} decided tenders (${winRate.lost} lost). Cancelled and pending tenders are not counted.`
            : 'No decided tenders yet. Cancelled and pending tenders are not counted.'}
        </Typography>
      ) : null}
      {loading ? <CircularProgress /> : null}
      {error ? <Typography color="error">{error}</Typography> : null}
      {!loading && !error && rows.length === 0 ? (
        <Typography>No requests found.</Typography>
      ) : null}
      {!loading && !error && rows.length > 0 ? (
        <Table columns={columns} rows={rows} rowId="id" rowsPerPage={10} />
      ) : null}
    </CommonModal>
  );
};

export default TenderRequestsModal;
